import BaseEnumService, * as baseEnumService from "@edaz/services/baseEnumService";
import EnumRepository, { SupportedEnumAPIVersions } from "repositories/enumRepository";
import Enum from "models/enum";
import { action, observable, set, when } from "mobx";
import { bound } from "@frui.ts/helpers";

export type EnumTypes = "countries" | "exemption_reasons" | "charge_validity_rules" | "fuel_types" | "charge_types";

/**
 * Enum service
 */
export default class EnumService extends BaseEnumService<Enum, EnumTypes> {
  @observable initializationInProgress = false;
  @observable isInitialized = false;
  @observable enums: baseEnumService.EnumContainer<Enum> = {};
  @observable currentlyFetchingEnums: Map<EnumTypes, boolean> = new Map();
  protected enumCache = {};

  constructor(protected repository: EnumRepository) {
    super(repository);
  }

  @action.bound
  async initialize() {
    if (this.initializationInProgress) {
      await when(() => !this.initializationInProgress);
      return;
    }

    this.initializationInProgress = true;
    await Promise.all([
      this.fetchEnum("countries"),
      this.fetchEnum("fuel_types"),
      this.fetchEnum("charge_types"),
      this.fetchEnum("charge_validity_rules"),
      this.fetchEnum("exemption_reasons"),
    ]);
    this.isInitialized = true;
    this.initializationInProgress = false;
  }

  @bound
  async startInitialization() {
    this.initialize();
  }

  isEnumLoaded(enumName: EnumTypes) {
    return this.enums.hasOwnProperty(enumName);
  }

  getEnumAPIVersionByName(enumName: EnumTypes): SupportedEnumAPIVersions {
    switch (enumName) {
      case "countries":
        return 3.0;
      case "exemption_reasons":
        return 3.0;
      case "charge_validity_rules":
        return 3.0;
      case "fuel_types":
        return 3.0;
      case "charge_types":
        return 3.0;
    }
  }

  async fetchEnum(enumName: EnumTypes) {
    if (!this.currentlyFetchingEnums.get(enumName)) {
      this.currentlyFetchingEnums.set(enumName, true);
      const apiVersion = this.getEnumAPIVersionByName(enumName);
      const enumValues = await this.repository.getEnum(enumName, apiVersion);
      set(this.enums, enumName, enumValues);
      this.currentlyFetchingEnums.set(enumName, false);
    } else {
      await when(() => this.currentlyFetchingEnums.get(enumName) === false);
    }
  }

  values(name: EnumTypes): Enum[] {
    if (!this.enums[name]) {
      this.fetchEnum(name);
      return [];
    }

    return this.enums[name] || [];
  }

  async lazyValues(name: EnumTypes): Promise<any> {
    if (!this.enums[name]) {
      await this.fetchEnum(name);
    }

    return this.values(name);
  }

  async lazyValue(name: EnumTypes, code: string | number): Promise<Enum | undefined> {
    if (!this.enums[name]) {
      await this.fetchEnum(name);
    }

    return super.value(name, code);
  }
}
